import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { castArray, compact, isEmpty } from 'lodash';
import * as React from 'react';
import { Fragment, ReactNode } from 'react';
import { OrderCategory, TProductCost } from '../../../../../api/endpoints/Order';
import { TOrderProduct } from '../../../../../api/endpoints/OrderProduct';
import { TApiParameter } from '../../../../../api/endpoints/Parameter';
import { TBcType } from "../../../../../api/endpoints/Subset";
import { makeResolution } from '../../../../../entities/Resolution';
import {
  formatAreas,
  formatBaseTimes,
  formatBinaryCapacityUnit,
  formatDirection,
  formatFrequency,
  formatHindcastDay, formatLevelist,
  formatResolution,
  formatRun
} from '../../../../../utils/Format';
import EnsembleMembers from "../../../../ProductFormatters/EnsembleMembers";
import Run from '../../../../ProductFormatters/Run';
import Steps from '../../../../ProductFormatters/Steps';
import TimeStep from '../../../../ProductFormatters/TimeStep';
import ParamLevelistFormatter from './ParamLevelistFormatter';
import ParamQuantileFormatter from './ParamQuantileFormatter';

interface IProps {
  dataSetName: string;
  bcType: TBcType;
  params: {
    [shortname: string]: TApiParameter
  };
  product: TOrderProduct<OrderCategory.REALTIME>;
  cost?: TProductCost;
  timestamp?: string;
  epuFootnoteRef?: string;
}

const grey = "#f0f0f0";
const paddingXs = "2mm";
const paddingSm = "3mm";
const fontItalic = "Helvetica-Oblique";

const styles = StyleSheet.create({
  itemRow: {
    paddingBottom: paddingXs
  },
  title: {
    fontFamily: fontItalic
  },
  colLeft: {
    width: "30%"
  },
  colRight: {
    width: "70%",
    paddingLeft: paddingXs
  },
  flexRow: {
    flexDirection: "row"
  },
  flexWrap: {
    flexWrap: "wrap"
  },
  productWrap: {
    flexDirection: "column",
    lineHeight: 1.5
  },
  infoLeft: {
    width: "70%",
    padding: paddingSm,
    borderRight: `1pt solid ${grey}`
  },
  infoLeftNoCost: {
    width: '100%',
    borderRight: 'none'
  },
  infoRight: {
    width: "30%",
    padding: paddingSm
  },
  nameHeader: {
    width: "70%",
    borderRight: "1pt solid white",
    padding: paddingSm,
    lineHeight: 1.5
  },
  nameHeaderNoCost: {
    width: '100%'
  },
  costHeader: {
    width: "30%",
    paddingLeft: paddingSm
  },
  productHeader: {
    backgroundColor: grey,
    alignItems: "center"
  }
});

const ProductAttribute = (props: {
  children: ReactNode;
  label: string;
}) => {
  const { children, label } = props;

  return (
    <View style={{ display: 'flex', flexDirection: 'row', marginBottom: '2mm' }}>
      <View style={{ width: '30%' }}>
        <Text>
          {label}:
        </Text>
      </View>

      <View style={{ width: '70%' }}>
        {children}
      </View>
    </View>
  );
};


const ProductDetails = (props: IProps) => {
  const { dataSetName, product, cost, params, bcType, timestamp } = props;
  const productData = product.data;

  const set = {
    name: dataSetName
  };
  const legs = productData.legs ?? [];


  return (
    <View style={styles.productWrap}>
      <View style={[styles.productHeader, styles.flexRow]}>
        <View style={compact([styles.nameHeader, !cost && styles.nameHeaderNoCost])}>
          <View style={styles.productName}>
            <Text>{productData.name}</Text>
          </View>

          <View style={styles.flexRow}>
            <Text>{set.name}</Text>
          </View>
        </View>
        {
          cost &&
          <View style={styles.costHeader}>
            <Text>Cost</Text>
          </View>
        }
      </View>

      <View style={styles.flexRow}>
        <View style={compact([styles.infoLeft, !cost && styles.infoLeftNoCost])}>
          {
            productData.time &&
            <ProductAttribute label="Base times">
              <Text>{formatBaseTimes(productData.time).join(', ')}</Text>
            </ProductAttribute>
          }

          {
            legs.map((leg, index) => {
              return (
                <Fragment key={index}>
                  {
                    legs.length > 1 &&
                    <Text>Leg {index + 1}</Text>
                  }

                  {
                    leg.grid &&
                    <ProductAttribute label="Grid">
                      <Text>{formatResolution(makeResolution(leg.grid))}</Text>
                    </ProductAttribute>
                  }

                  {
                    leg.step &&
                    <ProductAttribute label="Forecast time-steps">
                      <Steps values={leg.step} bcType={bcType} selectedTimes={productData.time} timestamp={timestamp} as={Text} />
                    </ProductAttribute>
                  }
                </Fragment>
              );
            })
          }

          {
            productData.grid &&
            <ProductAttribute label="Grid">
              <Text>{formatResolution(makeResolution(productData.grid))}</Text>
            </ProductAttribute>
          }

          {
            productData.step &&
            <ProductAttribute label="Forecast time-steps">
              <Steps values={productData.step} bcType={bcType} selectedTimes={productData.time} timestamp={timestamp} as={Text} />
            </ProductAttribute>
          }

          {
            productData.number &&
            <ProductAttribute label="Ensemble members">
              <EnsembleMembers values={productData.number} as={Text} />
            </ProductAttribute>
          }

          {
            !isEmpty(productData.area) &&
            <ProductAttribute label="Areas">
              {
                castArray(formatAreas(productData.area)).map(area => <Text key={area}>{area}</Text>)
              }
            </ProductAttribute>
          }

          {
            productData.location &&
            <ProductAttribute label="Number of points">
              <Text>{productData.location}</Text>
            </ProductAttribute>
          }

          {
            productData.time_step &&
            <ProductAttribute label="Base times">
              <Text><TimeStep values={productData.time_step} /></Text>
            </ProductAttribute>
          }

          {
            productData.param &&
            <ProductAttribute label="Parameters">
              <Text>{productData.param.join(' ')}</Text>
            </ProductAttribute>
          }

          {
            productData.levelist &&
            <ProductAttribute label="Levels">
              <Text>{formatLevelist(productData.levelist)}</Text>
            </ProductAttribute>
          }

          {
            productData.param_levelist &&
            <ProductAttribute label="Parameters">
              <ParamLevelistFormatter value={productData.param_levelist} params={params} />
            </ProductAttribute>
          }

          {
            productData.param_quantile &&
            <ProductAttribute label="Parameters">
              <ParamQuantileFormatter value={productData.param_quantile} params={params} />
            </ProductAttribute>
          }

          {
            productData.use &&
            <ProductAttribute label="Runs">
              <Text>
                {
                  productData.use.map(formatRun).join(', ')
                }
              </Text>
            </ProductAttribute>
          }

          {
            productData.use_step &&
            <ProductAttribute label="Runs">
              <Run values={productData.use_step} as={Text} />
            </ProductAttribute>
          }

          {
            productData.fcmonth &&
            <ProductAttribute label="Months">
              <Text>{productData.fcmonth.join(' ')}</Text>
            </ProductAttribute>
          }

          {
            productData.channel &&
            <ProductAttribute label="Channels">
              <Text>{productData.channel.join(' ')}</Text>
            </ProductAttribute>
          }

          {
            productData.direction &&
            <ProductAttribute label="Directions">
              <Text>{productData.direction.map(formatDirection).join(', ')}</Text>
            </ProductAttribute>
          }

          {
            productData.frequency &&
            <ProductAttribute label="Frequencies">
              <Text>{productData.frequency.map(formatFrequency).join(', ')}</Text>
            </ProductAttribute>
          }

          {
            productData.hindcast && productData.hindcast.length &&
            <ProductAttribute label="Hindcast">
              <Text>{productData.hindcast.map(formatHindcastDay).join(', ')}</Text>
            </ProductAttribute>
          }
        </View>

        {
          cost &&
          <View style={styles.infoRight}>
            <View>
              <View style={[styles.itemRow, styles.flexRow]}>
                <View style={{ width: "50%" }}>
                  <Text style={styles.title}>Volume daily</Text>
                </View>

                <View style={{ ...styles.colRight, width: "50%" }}>
                  <Text>
                    {formatBinaryCapacityUnit(cost.average_daily_volume)}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        }
      </View>
    </View>
  )
}

export default ProductDetails;
